import { forwardRef, useMemo } from 'react'
import { ChartLegend } from './ChartLegend'
import { EChart, type EChartHandle } from './EChart'
import { buildGanttOption, ganttHeight, phaseColor, type GanttInput, type GanttRow, type GanttSegment } from './ganttOption'
import { useChartTheme } from './useChartTheme'

export interface GanttTimelineProps extends GanttInput {
  height?: number
  loading?: boolean
  ariaLabel?: string
  className?: string
  texture?: boolean
  onSegmentClick?: (row: GanttRow, segment: GanttSegment) => void
  /** Клик по подписи строки (названию темы) — обычно открывает карточку объекта. */
  onRowClick?: (row: GanttRow) => void
  /** Скрыть HTML-легенду фаз (страница показывает свою). */
  hideLegend?: boolean
}

/** Сезонный обратный отсчёт: «запустить до» → «в ассортименте» → «пик» по темам. */
export const GanttTimeline = forwardRef<EChartHandle, GanttTimelineProps>(function GanttTimeline(
  { height, loading, ariaLabel, className, texture, onSegmentClick, onRowClick, hideLegend, ...input },
  ref,
) {
  const theme = useChartTheme()
  const option = useMemo(
    () => buildGanttOption(input, theme),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, input.rows, input.phases, input.today, input.from, input.to, input.markers, input.labelWidth],
  )
  const legend = input.phases.map((phase, index) => ({ label: phase.label, color: phaseColor(theme, index, input.phases.length) }))
  return (
    <div className={className}>
      {!hideLegend && <ChartLegend items={legend} className="mb-2" />}
      <EChart
        ref={ref}
        option={option}
        height={height ?? ganttHeight(input.rows.length)}
        loading={loading}
        ariaLabel={ariaLabel}
        texture={texture}
        onMarkClick={(params) => {
          if (params.componentType === 'yAxis') {
            const key = String(params.value ?? '')
            const row = key.startsWith('r') ? input.rows[Number(key.slice(1))] : undefined
            if (row) onRowClick?.(row)
            return
          }
          if (params.componentType !== 'series') return
          const value = (params.data ?? params.value) as unknown
          if (!Array.isArray(value)) return
          const row = input.rows[value[0] as number]
          const segment = row?.segments[value[4] as number]
          if (row && segment) onSegmentClick?.(row, segment)
        }}
      />
    </div>
  )
})

export default GanttTimeline
